"use client";

import dynamic from "next/dynamic";
import { createContext, useContext, useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell, {
  type BirthToolFormShellProps,
} from "@/components/tool-shell/BirthToolFormShell";

import type { YogaCheckId } from "./yoga-check";
import { YOGA_CHECK_FIELD_IDS, YOGA_CHECK_FRAME, yogaCheckFormProps } from "./yoga-check-form";

// The /yoga/<slug>/ chart-check boundary (the #2460 pattern): the server page
// imports only this module, and it owns the one dynamic() to the island. The
// form, place search, kundali client, verdict and analytics load as an async
// chunk; the first load gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer
// (this branch's useDeferredToolActivation has both), so a reader scrolling
// the guide never downloads the island and the deploy's body pointerdown smoke
// loads nothing; a press, focus or submit on the shell does.
//
// The slot reserves the guide's tallest state and every state fills it, so
// neither shell -> form nor a slow engine reply -> verdict (or error) moves the
// page below. translate="no" covers the shell, form and verdict, which
// re-render on "Check another birth date" (tests/translate-safe-islands.test.ts).

/**
 * Each guide's reserve: its tallest natural slot height, measured 2026-10-06
 * on next dev with Playwright (/kundali mocked with a 2 s reply) at 320, 360,
 * 640 and 1280 px wide, rounded up.
 * - Gajakesari, Budhaditya (birth time optional): the form with "Don't know
 *   your birth time?" open, a picked place and the longest needs-time message,
 *   685 at 320 and 670 at 360; 564 from 640 up. Their verdicts reach 473.
 * - Malavya: the unavailable error, 538 on phones; 420 from 640 up.
 * - Viparita: three lord rows whose flags wrap, 537 on phones and 545 from
 *   640 up.
 * - Neecha Bhanga: an exalted canceller with the sign lord also in a kendra,
 *   706 at 320 (2008-12-19 06:16, Mumbai), 639 at 360 (1985-07-26 11:19,
 *   Kolkata) and 577 from 640 up. That is the ceiling: the verdict rendered
 *   with the page's CSS for every Lagna, Moon, canceller and sign-lord
 *   placement the engine would report, with each combust and retrograde
 *   flag, reaches no higher.
 */
const SLOT_RESERVE: Readonly<Record<YogaCheckId, string>> = {
  gajakesari: "min-h-[700px] sm:min-h-[580px]",
  budhaditya: "min-h-[700px] sm:min-h-[580px]",
  malavya: "min-h-[600px] sm:min-h-[500px]",
  vipreet_raja: "min-h-[600px] sm:min-h-[560px]",
  neecha_bhanga: "min-h-[720px] sm:min-h-[600px]",
};

/**
 * The shell's props for this guide. dynamic()'s loading placeholder receives
 * no props from the page, and the birth-time rule differs per guide, so the
 * slot hands them down through context; the default is the stricter form.
 */
const ShellProps = createContext<BirthToolFormShellProps>({
  ...yogaCheckFormProps(true),
  className: YOGA_CHECK_FRAME,
});

function LoadingShell() {
  return <BirthToolFormShell {...useContext(ShellProps)} />;
}

const YogaChartCheck = dynamic(() => import("./YogaChartCheck"), {
  ssr: false,
  loading: () => <LoadingShell />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: YOGA_CHECK_FIELD_IDS.date,
  tob: YOGA_CHECK_FIELD_IDS.time,
  place: YOGA_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || YOGA_CHECK_FIELD_IDS.date;
}

export interface YogaChartCheckLazyProps {
  engineYogaId: YogaCheckId;
  /** The guide's name as its H1 writes it. */
  yogaName: string;
  requireBirthTime: boolean;
}

export default function YogaChartCheckLazy({
  engineYogaId,
  yogaName,
  requireBirthTime,
}: YogaChartCheckLazyProps) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const shellProps = { ...yogaCheckFormProps(requireBirthTime), className: YOGA_CHECK_FRAME };

  // The shell's own onActivate cannot tell a keyboard focus from a touch, so
  // the slot reads the intent in the capture phase: focus follows the visitor
  // into the real form after a keyboard focus, a mouse press or a submit, and
  // never after a touch, which may be the start of a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(YOGA_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-yoga-check-slot=""
      data-engine-yoga={engineYogaId}
      className={`flex flex-col ${SLOT_RESERVE[engineYogaId]}`}
      {...shellIntent}
    >
      {active ? (
        <ShellProps.Provider value={shellProps}>
          <YogaChartCheck
            engineYogaId={engineYogaId}
            yogaName={yogaName}
            requireBirthTime={requireBirthTime}
            focusFieldId={focusFieldId}
          />
        </ShellProps.Provider>
      ) : (
        <BirthToolFormShell {...shellProps} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
